import { ReactComponent as NoDataIcon } from '@assets/icons/V4/no-data.svg';
import { ReactComponent as SettingsIcon } from '@assets/icons/V4/settings-icon.svg';
import IconButton from '@components/V4/IconButton';
import Table from '@components/V4/Table/Table';
import {
  Enrollment,
  ENROLLMENT_STATUS_TYPES,
  GET_SESSION_COMPLETED_STATUSES,
} from '@shared/common';
import useClientPagination from 'apps/agora/src/hooks/useClientPagination';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import { useContext, useMemo } from 'react';
import SessionModalSection from '../../../DetailsModalSection';
import { MentorDetailsPageContext } from '../../MentorDetailsPage';

const COMPLETED_STATUSES = GET_SESSION_COMPLETED_STATUSES(true);

type PartialEnrollment = {
  enrollment: Partial<Enrollment>;
  assignedCount: number;
  assignedCompletedCount: number;
};

const PartialEnrollmentsTab = () => {
  const { mentor, onEnrollmentModalOpen, onStudentModalOpen } =
    useContext(MentorDetailsPageContext);

  const partialEnrollments = useMemo<PartialEnrollment[]>(() => {
    if (!mentor?._id) return [];

    return (mentor.enrollments ?? [])
      .filter(
        (enrollment) => enrollment.user?._id !== mentor._id && enrollment.mentor?._id !== mentor._id
      )
      .map((enrollment) => {
        const assignedSessions =
          enrollment.sessions?.filter((session) => session.assignedMentor?._id === mentor._id) ??
          [];

        return {
          enrollment,
          assignedCount: assignedSessions.length,
          assignedCompletedCount: assignedSessions.filter((session) => {
            const lastStatusValue = session.statuses?.[session.statuses.length - 1]?.value;
            return lastStatusValue !== undefined && COMPLETED_STATUSES.includes(lastStatusValue);
          }).length,
        };
      })
      .filter((row) => row.assignedCount > 0);
  }, [mentor?._id, mentor?.enrollments]);

  const [paginatedEnrollments, pagination] = useClientPagination({
    data: partialEnrollments,
    shouldOnlyShowPages: true,
    initialPageSize: 10,
  });

  return (
    <SessionModalSection className="mt-6" sectionTitle="Partial Enrollments">
      {paginatedEnrollments?.length ? (
        <Table size="small" pagination={pagination}>
          <Table.Header>
            <Table.Row isHeader>
              <Table.HeaderCell>Student</Table.HeaderCell>
              <Table.HeaderCell>Course</Table.HeaderCell>
              <Table.HeaderCell>Primary Mentor</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Assigned Sessions</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedEnrollments.map(({ enrollment, assignedCount, assignedCompletedCount }) => {
              const lastStatus = enrollment.statuses?.[enrollment.statuses.length - 1]?.status;

              return (
                <Table.Row key={enrollment._id}>
                  <Table.Cell
                    onClick={() => enrollment.user?._id && onStudentModalOpen(enrollment.user._id)}
                  >
                    {enrollment.user?.fullName ?? 'n/a'}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-[9px] h-[9px] rounded-full"
                        style={{ backgroundColor: enrollment.course?.color }}
                      />
                      {enrollment.course?.title ?? 'n/a'}
                    </div>
                  </Table.Cell>
                  <Table.Cell>{enrollment.mentor?.fullName ?? 'n/a'}</Table.Cell>
                  <Table.Cell>
                    {lastStatus !== undefined ? ENROLLMENT_STATUS_TYPES[lastStatus] : 'n/a'}
                  </Table.Cell>
                  <Table.Cell>{`${assignedCompletedCount} / ${assignedCount} completed`}</Table.Cell>
                  <Table.Cell>
                    <IconButton
                      size="small"
                      variant="ghost"
                      icon={<SettingsIcon />}
                      onClick={() => onEnrollmentModalOpen(enrollment._id ?? '')}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <EmptyStateCard text="No Existing Data" icon={<NoDataIcon className="text-customGrey" />} />
      )}
    </SessionModalSection>
  );
};

export default PartialEnrollmentsTab;
